import VueCookie from 'vue-cookie';
import { ActionTree } from 'vuex';
import { normalize } from 'normalizr';
import { ClassroomModel, CourseInfo, ListModel, UserInfo } from '@descomplica/dex-models';
import {
  listSession as listSessionSchema,
  user as userSchema,
} from '@descomplica/dex-store/lib/normalizr_schema';
import { ENDPOINTS } from '@/settings/endpoints';
import { chimeraQuery, dexMutation, dexQuery } from '@/helpers/graphql';
import { UserState } from './state';
import RootState from '@/store/root_state';
import {
  allListSessionsQuery,
  dexUpdateUserMutation,
  getForumParticipantsInfoQuery,
  userInfoByEmail,
  userStartedEnrollments,
  syncUserCoursesMutation,
} from '../../queries/dexQueries';
import { chimeraUserInformationQuery } from '../../queries/chimeraQueries';
import Analytics from '@/helpers/analytics';
import SentryClient from '@/helpers/sentry';
import { getCourseInfoCategory } from '@/helpers/category';
import { graphqlQuery, MICROSERVICES_ENUM } from '@/helpers/apollo';
import { getPostgradStudyPlanningAvailabilityForCourseQuery } from '@/store/queries/westerosPostgradStudyPlanningQueries';
import { getChimeraToken } from '@/helpers/get-tokens';
import { getUndergradContractsFromEpona } from '@/helpers/graphql/epona';
import ContractExtensionRequestsService from '@/helpers/postgradContractExtension/request';
import { ChimeraCourseInfo } from '@/models/chimera/ChimeraCourseModel';
import { EVENT_LABELS } from '@/helpers/analytics/labels';

// mutations
const SIGNOUT = 'SIGNOUT';
const SET_USER = 'SET_USER';
const SET_TOKEN = 'SET_TOKEN';

type ChimeraRole = { name: string };

type ChimeraMe = {
  name: string;
  firstName: string;
  lastName: string;
  pictureSHA: string;
  featureFlags: {
    'redirect-to-classrooms': boolean;
    'postgrad-assessments-card': boolean;
    'postgrad-tcc': boolean;
    'social-metaverse': boolean;
    'new-terms-update-dialog': boolean;
    'public-video-notes': boolean;
    'undergrad-classrooms-grades': boolean;
    'equal-web-accessibility-widget': boolean;
    'rename-periods-to-modules': boolean;
    'undergrad-classrooms-due-date-warning': boolean;
    'grade-activities-via-ai': boolean;
    'postgrad-contract-extension': boolean;
    'postgrad-students-documents': boolean;
    'postgrad-course-certificate': boolean;
    'postgrad-trial': boolean;
  };
  courses: ChimeraCourseInfo[];
  expiredCourses: ChimeraCourseInfo[];
  settings: {
    experiments: {
      migrationToClassroomsEnabled: boolean;
    };
  };
  roles: ChimeraRole[];
};

type DexUser = {
  id: number;
  name: string;
  email: string;
};

type Course = {
  name: string;
  category: string;
};

function isStaffMember(roles: Array<{ name: string }>) {
  const staffMembersList = ['admin', 'tutor'];
  let isStaff = false;
  roles.forEach((element) => {
    isStaff = staffMembersList.indexOf(element.name) > -1;
  });
  return isStaff;
}

const updateDexUserName = async (user: UserInfo & { id: number }, me: ChimeraMe) => {
  if (me != null) {
    user.firstName = me.firstName;
    user.lastName = me.lastName;
    user.pictureSHA = me.pictureSHA;
    user.isStaffMember = isStaffMember(me.roles);

    if (user.name !== me.name) {
      await dexMutation(dexUpdateUserMutation, {
        userId: user.id,
        name: me.name,
      });
    }
  }
};

const identifyUser = (payload: {
  me: ChimeraMe;
  dexUser: DexUser;
  collegeCourses: CourseInfo[];
}) => {
  const { me, dexUser, collegeCourses } = payload;
  const chimeraCourses: ChimeraCourseInfo[] = me.courses;
  const courses: Course[] = [];

  chimeraCourses.forEach((course) => {
    courses.push({
      name: course.name,
      category: course.category.slug,
    });
  });

  collegeCourses.forEach((course) => {
    courses.push({
      name: course.name,
      category: getCourseInfoCategory(course) || '',
    });
  });

  const categories: string[] = courses.map((course) => course.category);
  const categoriesSet: Set<string> = new Set<string>(categories);
  const uniqueCatogories: string[] = Array.from<string>(categoriesSet);

  const flags = Analytics.flagsBuilder(me.featureFlags);
  Analytics.identify({
    id: dexUser.id,
    name: dexUser.name,
    email: dexUser.email,
    flags,
    courses: courses.map((course) => course.name),
    categories: uniqueCatogories,
  });
};

export const getChimeraUserInfo = async (commit: Function): Promise<ChimeraMe | undefined> => {
  const response = await chimeraQuery(chimeraUserInformationQuery);
  const { me }: { me: ChimeraMe } = response.data.chimera;

  if (!me) return undefined;

  const expiredChimeraCourses = me.expiredCourses.reduce<ChimeraCourseInfo[]>(
    (expiredCourses, expiredCourse) => {
      if (!me.courses.find((c) => c._id === expiredCourse._id)) {
        expiredCourses.push(expiredCourse);
      }

      return expiredCourses;
    },
    [],
  );

  commit('SET_SHOW_DUE_DATE_WARNING', me.featureFlags['undergrad-classrooms-due-date-warning']);
  commit('SET_SHOULD_DISPLAY_SOCIAL_METAVERSE', me.featureFlags['social-metaverse']);
  commit('SET_SHOW_POSTGRAD_ASSESSMENTS_CARD', me.featureFlags['postgrad-assessments-card']);
  commit('SET_SHOW_POSTGRAD_TCC_ASSESSMENT', me.featureFlags['postgrad-tcc']);
  commit('SET_CAN_VIEW_TERMS_OF_USE', me.featureFlags['new-terms-update-dialog']);
  commit('SET_CHIMERA_USER_COURSES', me.courses);
  commit('SET_CHIMERA_USER_MOST_RECENT_CONTRACT', me.courses);
  commit('SET_CHIMERA_USER_EXPIRED_COURSES', expiredChimeraCourses);
  commit('SET_SHOULD_DISPLAY_PUBLIC_VIDEO_NOTES', me.featureFlags['public-video-notes']);
  commit(
    'SET_CAN_VIEW_UNDERGRAD_CLASSROOMS_GRADES',
    me.featureFlags['undergrad-classrooms-grades'],
  );
  commit('SET_SHOULD_SHOW_EQUAL_WEB_WIDGET', me.featureFlags['equal-web-accessibility-widget']);
  commit('SET_SHOULD_DISPLAY_PERIODS_RENAMED', me.featureFlags['rename-periods-to-modules']);
  commit('SET_GRADE_ACTIVITIES_VIA_AI', me.featureFlags['grade-activities-via-ai']);
  commit(
    'SET_CAN_VIEW_POSTGRAD_CONTRACT_EXTENSION_BUTTON',
    me.featureFlags['postgrad-contract-extension'],
  );
  commit('SET_CAN_VIEW_POSTGRAD_STUDENT_DOCUMENTS', me.featureFlags['postgrad-students-documents']);
  commit('SET_CAN_VIEW_POSTGRAD_CERTIFICATE', me.featureFlags['postgrad-course-certificate']);
  commit('SET_SHOULD_GET_TRIAL', me.featureFlags['postgrad-trial']);

  return me;
};

export const dexAuthentication = async (d: string, commit: Function): Promise<any> => {
  const cookieResponse = await fetch(`${ENDPOINTS.dexAuthentication}`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      d,
    }),
  });

  if (cookieResponse.status !== 200) {
    commit(SIGNOUT);
    return undefined;
  }

  const response = await cookieResponse.json();
  return response;
};

export const enrollUserInMissingCourses = async () => {
  const descomplicaToken = getChimeraToken();

  await dexMutation(syncUserCoursesMutation, {
    userToken: descomplicaToken,
  });
};

export const checkPostgradContractsExtensionsUpdates = async () => {
  await ContractExtensionRequestsService.checkAndExtendContracts();
};

const actions: ActionTree<UserState, RootState> = {
  async VALIDATE({ commit, dispatch }): Promise<boolean> {
    const d = VueCookie.get('d', { domain: '.descomplica.com.br' });
    if (!d) {
      commit(SIGNOUT);
      return false;
    }

    const dexAuthResponse = await dexAuthentication(d, commit);

    if (!dexAuthResponse) {
      // we are now sending the error to Sentry using the api method capture error
      // https://docs.sentry.io/platforms/javascript/vue/
      SentryClient.captureError(new Error('Dex Auth failed'));

      return false;
    }

    const { token, user } = dexAuthResponse;

    if (user && token) {
      commit(SET_TOKEN, token);
      commit(SET_USER, user);

      await Analytics.registerIfIsInTrial();
      Analytics.identify({
        id: user.id,
        name: user.name,
        email: user.email,
      });
      dispatch('GET_ALUMNI_PROFILE', user);
      return true;
    }

    commit(SIGNOUT);
    return false;
  },

  async PULL_USER_INFO({ commit, state }): Promise<void> {
    const { user } = state;
    if (!user) return;

    await checkPostgradContractsExtensionsUpdates();
    try {
      await enrollUserInMissingCourses();
    } catch (err) {
      SentryClient.captureError(err);
      Analytics.trackEvent(EVENT_LABELS.postgradSyncUserCoursesError);
    }

    const [response, me, undergradContracts] = await Promise.all([
      dexQuery(userInfoByEmail, { userEmail: user.email }),
      getChimeraUserInfo(commit),
      getUndergradContractsFromEpona(),
    ]);
    if (!me) return;

    const dexUser = response.data.userByEmail;
    dexUser.contractsByUserId.nodes.push(...undergradContracts);

    updateDexUserName(dexUser, me);

    const normalizedResponse = normalize(response.data, {
      userByEmail: userSchema,
    });

    if (!normalizedResponse.entities.users) return;

    const userNormalized = normalizedResponse.entities.users[user.id];

    commit('SET_USER', userNormalized);
    commit('SET_CONTRACTS', normalizedResponse.entities);
    commit('SET_COURSES', normalizedResponse.entities);
    commit('SET_USERS', normalizedResponse.entities);

    if (dexUser) {
      identifyUser({
        me,
        dexUser,
        collegeCourses: Object.values(normalizedResponse.entities.courses || {}),
      });
    }
  },

  async PULL_USER_ENROLLMENTS({ commit, state, dispatch }, payload): Promise<void> {
    const { user } = state;
    const { courseId } = payload;

    if (!user) return;

    commit('SET_LOADING_USER_BASE_PROGRESS', { isLoading: true });

    const response = await dexQuery(userStartedEnrollments, {
      userEmail: user.email,
      currentCourseId: courseId,
    });

    const normalizedResponse = normalize(response.data, {
      userByEmail: userSchema,
    });

    if (!normalizedResponse.entities.users) return;

    const userEnrollments = normalizedResponse.entities.users[user.id];

    commit('SET_USER', { ...user, ...userEnrollments });
    commit('SET_USERS', normalizedResponse.entities);
    commit('SET_BOARDS', normalizedResponse.entities);
    commit('SET_BOARDS_CLASSROOMS', normalizedResponse.entities);
    commit('SET_CLASSROOMS', normalizedResponse.entities);
    commit('SET_ENROLLMENTS', normalizedResponse.entities);
    commit('SET_LIST_ITEMS', normalizedResponse.entities);
    commit('SET_LISTS', normalizedResponse.entities);
    commit('SET_QUESTIONS', normalizedResponse.entities);

    const { classrooms } = normalizedResponse.entities;

    if (classrooms) {
      const classroomIds = Object.keys(classrooms).map((classroomId: string) =>
        Number(classroomId),
      );
      dispatch('PULL_CLASSROOMS_PROGRESS', { classroomIds });
    }

    const { lists } = normalizedResponse.entities;
    if (lists) {
      const listIds = Object.keys(lists).map((listId: string) => Number(listId));
      dispatch('PULL_LISTS_PROGRESS', { listIds });
    }

    commit('SET_LOADING_USER_BASE_PROGRESS', { isLoading: false });
  },

  async PULL_USER_LISTS({ commit }, payload): Promise<void> {
    const { userId, classrooms } = payload;
    const listIds = classrooms.reduce((acc: Array<number>, classroom: ClassroomModel) => {
      return [
        ...acc,
        ...classroom
          .getLists()
          .map((list: ListModel) => list.id)
          .filter((listId: number) => listId),
      ];
    }, []);

    if (!userId || !listIds || listIds.length === 0) return;
    const response = await dexQuery(allListSessionsQuery, { userId, listIds });

    const normalizedResponse = normalize(response.data.allListSessions, {
      nodes: [listSessionSchema],
    });

    commit('SET_LIST_SESSIONS', normalizedResponse.entities);
    commit('UPDATE_LISTS_WITH_SESSIONS', normalizedResponse.entities);
  },

  async PULL_PARTICIPANTS_INFO({ commit }, { userIdList }: { userIdList: number[] }) {
    if (!userIdList.length) return;

    const response = await dexQuery(getForumParticipantsInfoQuery, { userIdList });

    const normalizedResponse = normalize(response.data.allUsers, {
      nodes: [userSchema],
    });

    commit('SET_USERS', normalizedResponse.entities);
  },

  async GET_POSTGRAD_STUDY_PLANNING_FEATURE_AVAILABILITY_FOR_COURSE({ commit }, courseId: number) {
    try {
      const response = await graphqlQuery(
        MICROSERVICES_ENUM.STUDYPLANNING,
        getPostgradStudyPlanningAvailabilityForCourseQuery,
        { courseId },
      );
      const isFeatureEnabledForCourse = response.data.getPostgradStudyPlanningAvailabilityForCourse;

      commit('SET_CAN_VIEW_POSTGRAD_STUDY_PLANNING', !!isFeatureEnabledForCourse);
    } catch (err) {
      SentryClient.captureError(err);

      commit('SET_CAN_VIEW_POSTGRAD_STUDY_PLANNING', false);
    }
  },
};

export default actions;
